import { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { CountryCode, isValidCountryCode } from '@campai/lib-common/utils/address'
import { Address } from '@campai/lib-common/entities'

import { env } from '@/env'
import {
    Button,
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxCollection,
} from '@/components/new/ui'

type AddressCompleteSelect = (value: Address) => void
type AddressCompleteManualAddress = () => void

type AddressOption = {
    value: string
    label: string
    data: MapboxFeature
}

type MapboxFeature = {
    geometry?: {
        coordinates?: [number, number]
    }
    properties?: {
        mapbox_id?: string
        full_address?: string
        name?: string
        context?: {
            country?: {
                country_code?: string
            }
            place?: {
                name?: string
            }
            postcode?: {
                name?: string
            }
        }
    }
}

type MapboxGeocodingResponse = {
    features?: MapboxFeature[]
}

type AddressInputProps = {
    className?: string
    autoFocus?: boolean
    onSelect: AddressCompleteSelect
    onManualAddress: AddressCompleteManualAddress
    restrictCountries?: CountryCode[]
    preferredCountry?: CountryCode
}

type PreferredPoint = { country: CountryCode; point: string | null }

export const AddressComplete = (props: AddressInputProps): JSX.Element => {
    if (!env.VITE_MAPBOX_TOKEN) {
        throw new Error('Missing VITE_MAPBOX_TOKEN so address input will not work')
    }

    const { autoFocus, className, onSelect, onManualAddress, restrictCountries, preferredCountry } = props

    const { t, i18n } = useTranslation()

    const [preferredPoint, setPreferredPoint] = useState<PreferredPoint | null>(null)
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [options, setOptions] = useState<AddressOption[]>([])
    const [loading, setLoading] = useState(false)
    const [hasSearchError, setHasSearchError] = useState(false)
    const requestControllerRef = useRef<AbortController | null>(null)
    const minQueryLength = 3

    const getCachedPreferredPoint = useCallback(async () => {
        if (!preferredCountry) {
            setPreferredPoint(null)
            return null
        } else if (preferredCountry && preferredPoint && preferredPoint.country === preferredCountry) {
            return preferredPoint
        } else {
            const params = new URLSearchParams({
                access_token: env.VITE_MAPBOX_TOKEN,
                types: 'country',
                limit: '1',
                q: preferredCountry,
            })

            const response = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params.toString()}`)

            if (!response.ok) {
                throw new Error(`Mapbox geocoding failed (${response.status} ${response.statusText})`)
            }

            const json = (await response.json()) as MapboxGeocodingResponse

            const newPreferredPoint: PreferredPoint = { country: preferredCountry, point: null }
            const coordinates = json.features?.[0]?.geometry?.coordinates
            if (coordinates?.length === 2) {
                newPreferredPoint.point = coordinates.join(',')
            }

            setPreferredPoint(newPreferredPoint)
            return newPreferredPoint
        }
    }, [preferredCountry, preferredPoint])

    const handleQueryOptions = useCallback(
        async ({ query, signal }: { query: string; signal: AbortSignal }): Promise<AddressOption[]> => {
            const cachedPreferredPoint = await getCachedPreferredPoint()

            const params = new URLSearchParams({
                access_token: env.VITE_MAPBOX_TOKEN,
                autocomplete: 'true',
                types: 'address',
                language: i18n.language,
                limit: '5',
                proximity: cachedPreferredPoint?.point || 'ip',
                q: query,
            })

            if (restrictCountries?.length) {
                params.set('country', restrictCountries.join(','))
            }

            const response = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params.toString()}`, {
                signal,
            })

            if (!response.ok) {
                throw new Error(`Mapbox geocoding failed (${response.status} ${response.statusText})`)
            }

            const json = (await response.json()) as MapboxGeocodingResponse

            return (json.features ?? []).flatMap((feature) => {
                const value = feature.properties?.mapbox_id
                const label = feature.properties?.full_address

                return value && label ? [{ value, label, data: feature }] : []
            })
        },
        [getCachedPreferredPoint, i18n.language, restrictCountries]
    )

    const applySelectedAddress = useCallback(
        (option: AddressOption | null) => {
            if (!option?.data) return
            const properties = option.data.properties
            const street = properties?.name
            const city = properties?.context?.place?.name ?? ''
            const zip = properties?.context?.postcode?.name ?? ''
            const country = properties?.context?.country?.country_code?.toUpperCase()

            if (!street || !country || !isValidCountryCode(country)) {
                // -- something is invalid
                return
            }

            onSelect({
                country,
                zip,
                city,
                addressLine: street,
            })
        },
        [onSelect]
    )

    useEffect(() => {
        requestControllerRef.current?.abort()
        setHasSearchError(false)

        if (query.length < minQueryLength) {
            setOptions([])
            setLoading(false)
            return
        }

        const abortController = new AbortController()
        requestControllerRef.current = abortController
        setLoading(true)

        handleQueryOptions({ query, signal: abortController.signal })
            .then((nextOptions) => {
                if (abortController.signal.aborted) return
                setOptions(nextOptions)
            })
            .catch((error) => {
                if (error?.name !== 'AbortError') {
                    console.error(error)
                }
                if (!abortController.signal.aborted) {
                    setOptions([])
                    setHasSearchError(true)
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false)
                }
            })

        return () => {
            abortController.abort()
        }
    }, [handleQueryOptions, minQueryLength, query])

    const emptyContent = (() => {
        if (query.length < minQueryLength) {
            return t('components.addressForm.addressComplete.defaultPlaceholder')
        }
        if (loading) {
            return t('components.control.combobox.loading')
        }
        if (hasSearchError) {
            return t('components.addressForm.addressComplete.searchError')
        }
        return t('components.addressForm.addressComplete.noResults')
    })()

    const renderOption = useCallback((option: AddressOption) => {
        const [street, ...otherParts] = option.label.split(',').map((part) => part.trim())

        return (
            <div>
                <div className="font-medium">{street}</div>
                <div className="text-muted-foreground text-xs">{otherParts.join(', ')}</div>
            </div>
        )
    }, [])

    return (
        <div className={classNames(className, 'flex flex-col')}>
            <Combobox
                autoHighlight
                items={options}
                open={isOpen && query.length >= minQueryLength}
                onOpenChange={setIsOpen}
                inputValue={query}
                onValueChange={(option) => {
                    const nextOption = option as AddressOption | null

                    if (!nextOption?.data || query.length < minQueryLength) {
                        return
                    }

                    applySelectedAddress(nextOption)
                }}
                onInputValueChange={(nextQuery) => {
                    setQuery(nextQuery)
                    setIsOpen(nextQuery.length >= minQueryLength)
                }}
                itemToStringValue={(item) => (item as AddressOption).value}
                itemToStringLabel={(item) => (item as AddressOption).label}
                filter={null}
            >
                <ComboboxInput
                    className="flex-1"
                    placeholder={t('components.addressForm.addressComplete.defaultPlaceholder')}
                    autoFocus={autoFocus}
                    showTrigger={false}
                />
                <ComboboxContent initialFocus={false}>
                    <ComboboxEmpty>{emptyContent}</ComboboxEmpty>
                    <ComboboxList>
                        <ComboboxCollection>
                            {(item) => (
                                <ComboboxItem key={item.value} value={item}>
                                    {renderOption(item as AddressOption)}
                                </ComboboxItem>
                            )}
                        </ComboboxCollection>
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            <Button
                type="button"
                variant="link"
                size="sm"
                className="text-muted-foreground mt-1.5 h-auto w-fit px-0 py-0 text-xs font-medium"
                onClick={onManualAddress}
            >
                {t('components.addressForm.addressComplete.enterManually')}
            </Button>
        </div>
    )
}
